/**
 * Action Center "missed SMS" alerts — conversation intake + alert computation.
 *
 * Moved out of src/components/AgentPanel/AgentSidebar.vue for the 2026-09-16 redesign so the rules
 * are executed by tests instead of mirrored (src/store/__tests__/agent-missed-sms-alerts.test.js
 * loads THIS file). Detection is unchanged; the only addition is the office clearing setting —
 * who counts as having dismissed is decided by actionCenterReadState.isSmsAlertCleared.
 *
 * "Unread" (2026-09-16 fix) uses the SAME rule as the SMS Conversations list
 * (conversationsV2-filters.computeRoomUnreadCount): an inbound message newer than siteLastReadAt, or a
 * manual "mark unread". The root `unreadCount` field is no longer maintained on inbound (dev doc
 * CH060f4e5f…: unreadCount 0 while the list showed the thread unread), so reading it made the
 * 15–30 minute "Unread SMS" tier effectively unreachable.
 */
import { coerceToMs } from './firestoreTime';
import { isSmsAlertCleared } from './actionCenterReadState';
import { computeRoomUnreadCount } from '../store/conversationsV2-filters';

// ── Missed-SMS alert bounds ────────────────────────────────────────────────────
// These two work as a PAIR; changing one without the other reintroduces a bug.
//
// MAX_ALERT_AGE_DAYS is the real bound: an unanswered text from months ago is history,
// not a task, and surfacing it buries the ones that matter. Until 2026-07-20 no such
// bound existed — CONVERSATION_WINDOW was doing the job by accident, which is
// volume-dependent and therefore backwards: quiet sites leaked ancient threads while
// busy sites dropped recent ones. A prod audit that day found the 50-doc window hiding
// 18 unanswered patient messages from the previous week, while hiding 315 older than
// 90 days.
//
// So: raising CONVERSATION_WINDOW without MAX_ALERT_AGE_DAYS in place would flood the
// panel with hundreds of ancient cards. The age cap is what makes a larger window safe.
export const MAX_ALERT_AGE_DAYS = 7;
export const MAX_ALERT_AGE_MINS = MAX_ALERT_AGE_DAYS * 24 * 60;
// Sized so a busy site's last 7 days of threads fit; the age cap bounds what renders.
export const CONVERSATION_WINDOW = 300;

export const DEFAULT_CONFIRM_KEYWORDS = ['c', 'ok', 'k', 'yes', 'y', 'thank', 'thanks', 'confirm', 'confirmed', 'si'];

// Replies that mean "handled, nothing for the front desk to do" — the
// appointment-reminder confirmation words. Site-configurable via Apt
// Reminder settings; falls back to the same defaults that page ships with.
export function resolveConfirmKeywords(allSettings) {
  const fallback = DEFAULT_CONFIRM_KEYWORDS;
  try {
    const reminderSettings = allSettings?.APPT_REMINDER_SETTINGS?.PropertyValue;
    if (!reminderSettings) return fallback;
    const parsed = JSON.parse(reminderSettings);
    if (!parsed.textConfirmKeywords) return fallback;
    const parsedKeywords = parsed.textConfirmKeywords
      .split(',')
      .map((k) => k.trim().toLowerCase())
      .filter(Boolean);
    return parsedKeywords.length ? parsedKeywords : fallback;
  } catch (e) {
    return fallback;
  }
}

/**
 * One `conversations` doc → the local object the alert computation reads, or null when the
 * conversation can't need a reply (no inbound last message).
 */
export function intakeConversation(id, data) {
  if (!data) return null;
  // A conversation with no `lastMessage` has nothing to answer. Two ways it happens:
  // an abandoned "start new conversation" shell (createConversationInhouse mints the
  // doc with dateUpdated=now and no lastMessage), or a thread whose only traffic is an
  // automated send (smsEngine skips lastMessage when bumpConversation is false).
  //
  // This guard is required because inbound-ness below is inferred from the ABSENCE of
  // outbound markers: `data.lastMessage || {}` yields empty attributes, which reads as
  // INBOUND, and `timestampNum || 0` dates it to 1970 — producing a permanent
  // "UNANSWERED SMS, waiting 56 years" card with an empty preview. A brand-new
  // conversation sorts to the top of the dateUpdated window, so the phantom is the
  // FIRST thing the front desk sees.
  //
  // Safe to skip: audited prod 2026-07-20 — of 180 such docs (45 empty shells,
  // 135 outbound-only), ZERO contained an inbound patient message.
  // `timestampNum` is coerced rather than read raw. It is a plain number in 100% of the
  // 4,342 prod conversation documents audited 2026-07-21, so this changes nothing today —
  // it removes a silent failure mode. Read raw, a Firestore Timestamp here would make
  // `now - lastMessageTime` NaN, and NaN compares false against BOTH the 15-minute floor
  // and the 7-day cap, so the document would slip past both guards and render "NaNh NaNm".
  // coerceToMs turns any of Timestamp / {_seconds} / number / ISO string into ms, and
  // anything unparseable into null, which the existing guard below drops.
  //
  // Deliberately coerces ONLY timestampNum — no fallback to timestamp/date/dateCreated.
  // Falling back would change WHICH documents are eligible for an alert, which is a
  // behaviour change, not hardening. (teamio-mobile writes a lastMessage with no
  // timestampNum and direction:'outbound'; a fallback would surface those as inbound.)
  const lastMsg = data.lastMessage;
  if (!lastMsg) return null;
  const lastMessageTimeMs = coerceToMs(lastMsg.timestampNum);
  if (!lastMessageTimeMs) return null;
  const lastAttrs = lastMsg.attributes || {};
  const isInbound = !lastAttrs.outbound && !lastAttrs.sentViaAPI && !lastAttrs.reminderType;
  if (!isInbound) return null;
  // Per-employee dismiss map: { [employeeID]: timestamp }. Legacy
  // global `agentDismissedAt` is ignored in this code path now —
  // it lingers on old conversations but isn't read.
  const dismissedByMap = (data.agentDismissedBy && typeof data.agentDismissedBy === 'object')
    ? data.agentDismissedBy
    : {};
  // Office-wide unread, exactly as the SMS Conversations list computes it minus the per-browser parts
  // (the local read map, the list's unread baseline, and "this thread is open in my window").
  // lastBySite is false by construction: only threads whose last message is inbound reach this point.
  const unreadCount = computeRoomUnreadCount({
    lastInboundAtMs: coerceToMs(data.lastInboundAtMs),
    lastMessageTs: lastMessageTimeMs,
    lastBySite: false,
    lastRead: coerceToMs(data.siteLastReadAt || data.lastViewedAt),
    baseline: null,
    isSelected: false,
    markedUnreadAt: coerceToMs(data.markedUnreadBySite),
    serverUnread: typeof data.unreadCount === 'number' ? data.unreadCount : 0,
  });
  return {
    sid: id,
    patientName: data.patientName || data.friendlyName || 'Unknown',
    incomingPhoneNum: data.incomingPhoneNum || '',
    unreadCount,
    lastMessageTime: lastMessageTimeMs,
    lastMessageBody: lastMsg.body || lastMsg.content || '',
    stateCurrent: data.state?.current || '',
    agentDismissedBy: dismissedByMap,
  };
}

/**
 * Clear the dismiss map when a conversation closes — keeps Firestore tidy and avoids stale
 * per-employee entries piling up. The caller performs the write.
 */
export function shouldResetDismissMap(convo) {
  return !!convo && convo.stateCurrent === 'closed'
    && !!convo.agentDismissedBy && Object.keys(convo.agentDismissedBy).length > 0;
}

function isPinned(pinned, id) {
  if (!pinned) return false;
  return typeof pinned.has === 'function' ? pinned.has(id) : !!pinned[id];
}

/**
 * @param {Array} conversations intake objects
 * @param {Object} opts
 *   now         epoch ms
 *   employeeID  the viewer
 *   keywords    confirmation words (resolveConfirmKeywords)
 *   scope       ACTION_CENTER_DISMISS_SCOPE value ('office' | 'individual'; absent ⇒ office)
 *   pinned      Set / map of conversation SIDs whose dismissal is ignored — the viewer has a
 *               quick-reply draft open on that card, so a teammate clearing it must not yank it away
 */
export function computeMissedSmsAlerts(conversations, { now, employeeID, keywords, scope, pinned } = {}) {
  const confirmWords = Array.isArray(keywords) ? keywords : DEFAULT_CONFIRM_KEYWORDS;
  const alerts = [];

  for (const convo of conversations || []) {
    if (!convo) continue;
    if (convo.stateCurrent === 'closed') continue;
    // Dismissed AND no new inbound message has arrived since. A new message bumps
    // lastMessageTime above the stored dismiss timestamp, so the alert legitimately
    // resurfaces. Whose dismissal counts depends on the office setting.
    if (!isPinned(pinned, convo.sid) && isSmsAlertCleared(convo, { employeeID, scope })) continue;

    // Defense in depth against the phantom-card class: without a real timestamp the
    // elapsed maths below dates the message to 1970 and every such doc reads as
    // "unanswered". The intake already filters these out.
    if (!convo.lastMessageTime) continue;

    const elapsedMins = Math.floor((now - convo.lastMessageTime) / 60000);
    if (elapsedMins < 15) continue;
    // Explicit upper bound — see MAX_ALERT_AGE_DAYS. An old unanswered thread is
    // history, not a task, and it would bury the ones the front desk can still act on.
    if (elapsedMins > MAX_ALERT_AGE_MINS) continue;

    // Appointment-reminder confirmations ("c", "ok", "yes"…) are handled by
    // the reminder system — the front desk has nothing to reply to, so they
    // must never surface here. `lastMessageBody` is the last INBOUND message
    // (intake only keeps conversations whose lastMessage is inbound),
    // so it is the patient's own words.
    //
    // Do NOT go re-read the messages subcollection for this. The previous
    // version queried the newest 2 message docs and tested docs[0] without
    // checking direction — and docs[0] is routinely an OUTBOUND message we
    // sent after the patient replied (a later reminder, a review request).
    // Our own outgoing text never matches a keyword, so every confirmation
    // leaked through as an alert. Verified on dev 2026-07-20.
    const patientReply = (convo.lastMessageBody || '').trim().toLowerCase();
    if (confirmWords.some((kw) => kw === patientReply)) continue;

    let alertType = null;
    if (elapsedMins >= 30) alertType = 'unanswered';
    else if (convo.unreadCount > 0) alertType = 'unread';

    if (alertType) {
      alerts.push({
        conversationSID: convo.sid,
        type: alertType,
        patientName: convo.patientName,
        phone: convo.incomingPhoneNum,
        elapsed: elapsedMins,
        lastMessageBody: convo.lastMessageBody,
        lastMessageTime: convo.lastMessageTime,
        unreadCount: convo.unreadCount,
      });
    }
  }

  alerts.sort((a, b) => {
    if (a.type !== b.type) return a.type === 'unanswered' ? -1 : 1;
    return b.elapsed - a.elapsed;
  });

  return alerts;
}
